import { defineComponent as _defineComponent } from 'vue'

// el-dropdown is not globally registered; register it for this page (same as the
// other list pages do).
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

const __default__ = {
  name: 'UserManagement',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
}

import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate } from '@/plugins/dates'
import { relativeTime, olderThanDays } from '@/util/relativeTime'
import { pageOffset, syncPagingQuery, readPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import TabRail from '@/components/TabRail.vue'
import type { DataTableColumn } from '@/components/DataTable.vue'
import InviteUserDrawer from './InviteUserDrawer.vue'
import EditUserDrawer from './EditUserDrawer.vue'

type Role = { id: number; name: string; default_role: number }
type Group = { id: number; name: string }
type Status = 'invited' | 'active' | 'deactivated'
type Invite = { sent_at: string | null; expires_at: string | null; accepted_at: string | null; revoked_at: string | null } | null

interface UserRow {
  id: number
  email: string
  name: string
  label: string
  status: Status
  roleName: string
  activityText: string
  groups: Group[]
  raw: any
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

// Users list, server paged: GET /companies/{id}/users?count&offset&name&email&status&role&groups&sort&dir
// (users-get-all-users-for-company) returns { users, total_rows, status_counts } with each
// user's `status` (invited / active / deactivated), `role_name`, `user_groups` and `latest_invite`.
const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')
const baseUrl = `${API_LOCATION}companies/${companyId}`

const permissions: string[] = JSON.parse(localStorage.getItem('permissions') || '[]')
function can(scope: string) {
  return permissions.includes(scope)
}
let myId: number | null = null
try { myId = Number(JSON.parse(localStorage.getItem('profile') || 'null')?.id) || null } catch (e) { myId = null }

const loading = ref(false)
// Starts true so the table shows its skeleton until the first fetch lands.
const refreshing = ref(true)
const users = ref<any[]>([])
const roles = ref<Role[]>([])
const groups = ref<Group[]>([])
const totalRows = ref(0)

const inviteDrawer = ref(false)
const editDrawer = ref(false)
const selectedUser = ref<any>(null)

const perPageOptions = [10, 25, 50, 100]
const perPage = ref(10)
const currentPage = ref(1)
let didInitialFetch = false

const tabs = computed(() => (can('roles_update') ? [{ name: 'Users' }, { name: 'Roles' }] : [{ name: 'Users' }]))
function onTab(index: number) {
  if (index === 1) proxy.$router.push('/settings/roles')
}

// --- Sort and filters (server side) ----------------------------------------------
const sortOptions = [
  { key: 'status', label: 'Status' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Role' },
  { key: 'last_login', label: 'Last signed in' },
]
const sort = ref<{ key: string; dir: 'asc' | 'desc' }>({ key: 'status', dir: 'asc' })

function onSortChange(value: { key: string; dir: 'asc' | 'desc' }) {
  sort.value = value
  currentPage.value = 1
  if (didInitialFetch) fetchUsers()
}

const columns: DataTableColumn[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'role', label: 'Role', sortable: true },
  { key: 'groups', label: 'Groups' },
  { key: 'last_login', label: 'Last signed in', sortable: true },
]

const filterValues = ref<Record<string, any>>({
  name: '', email: '', status: ['invited', 'active'], role: '', groups: [],
  last_login: { mode: 'range', days: '', from: '', to: '' },
  suppressed: false,
})

const filterSchema = computed(() => [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'First or last name', operatorLabel: 'contains', primary: true },
  { key: 'email', type: 'text', label: 'Email', placeholder: 'Any part of the address', operatorLabel: 'contains', primary: true },
  {
    key: 'status', type: 'multiselect', label: 'Status', primary: true,
    options: [
      { label: 'Invited', value: 'invited' },
      { label: 'Active', value: 'active' },
      { label: 'Deactivated', value: 'deactivated' },
    ],
  },
  { key: 'role', type: 'select', label: 'Role', primary: true, options: roles.value.map(r => ({ label: r.name, value: r.name })) },
  { key: 'groups', type: 'multiselect', label: 'Group', primary: true, options: groups.value.map(g => ({ label: g.name, value: g.id })) },
  { key: 'last_login', type: 'daterange', label: 'Last signed in', primary: true },
  { key: 'suppressed', type: 'toggle', label: 'Email delivery blocked', primary: true },
])

const hasActiveFilters = computed(() => {
  const f = filterValues.value
  const statuses: string[] = Array.isArray(f.status) ? f.status : []
  const range = lastLoginRange()
  return !!f.name || !!f.email || !!f.role || (Array.isArray(f.groups) && f.groups.length > 0) || (statuses.length > 0 && statuses.length < 3) || !!range.from || !!range.to || !!f.suppressed
})
const noUsersAtAll = computed(() => totalRows.value === 0 && !hasActiveFilters.value)

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!didInitialFetch) {
    didInitialFetch = true
    fetchUsers()
    return
  }
  currentPage.value = 1
  fetchUsers()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchUsers()
}

function onPageChange(page: number) {
  currentPage.value = page
  fetchUsers()
}

// The date range chip has two modes: "last N days" (days) or an explicit from/to.
// Both become YYYY-MM-DD bounds in the browser's zone; the server compares
// wall-clock dates in that zone.
function localIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
function lastLoginRange(): { from: string; to: string } {
  const v = filterValues.value.last_login || {}
  if (v.mode === 'days' && Number(v.days) > 0) {
    const from = new Date()
    from.setDate(from.getDate() - (Number(v.days) - 1))
    return { from: localIsoDate(from), to: localIsoDate(new Date()) }
  }
  if (v.mode === 'range' && v.from && v.to) return { from: v.from, to: v.to }
  return { from: '', to: '' }
}

function listQuery(count: number, offset: number): string {
  const f = filterValues.value
  const params = new URLSearchParams()
  params.set('count', String(count))
  params.set('offset', String(offset))
  if (f.name) params.set('name', String(f.name).trim())
  if (f.email) params.set('email', String(f.email).trim())
  if (Array.isArray(f.status) && f.status.length) params.set('status', f.status.join(','))
  if (f.role) params.set('role', String(f.role))
  if (Array.isArray(f.groups) && f.groups.length) params.set('groups', f.groups.join(','))
  const range = lastLoginRange()
  if (range.from) params.set('last_login_from', range.from)
  if (range.to) params.set('last_login_to', range.to)
  if (range.from || range.to) params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone)
  if (f.suppressed) params.set('suppressed', '1')
  params.set('sort', sort.value.key)
  params.set('dir', sort.value.dir)
  return `${baseUrl}/users?${params.toString()}`
}

// --- Row shaping ---------------------------------------------------------------
function parseJson<T>(raw: any, fallback: T): T {
  if (!raw) return fallback
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw } catch (e) { return fallback }
}

// Last signed in column: a time for active users, invite timing for invited ones.
function activityTextFor(u: any, status: Status, invite: Invite): string {
  if (status === 'invited') {
    if (!invite || !invite.sent_at || invite.revoked_at) return 'Not signed in yet'
    const expired = invite.expires_at ? new Date(invite.expires_at).getTime() < Date.now() : false
    return expired ? 'Invite link expired' : `Invite expires ${relativeTime(invite.expires_at)}`
  }
  if (!u.last_login) return status === 'deactivated' ? '' : 'Not signed in yet'
  return olderThanDays(u.last_login, 30)
    ? formatGroupDate(u.last_login, { dayOfWeek: false })
    : relativeTime(u.last_login)
}

function toRow(u: any): UserRow {
  const invite = parseJson<Invite>(u.latest_invite, null)
  const status: Status = u.status === 'deactivated' || u.status === 'active' ? u.status : 'invited'
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
  return {
    id: u.id,
    email: u.email,
    name,
    label: name || u.email,
    status,
    roleName: u.role_name || u.type || '',
    activityText: activityTextFor(u, status, invite),
    groups: parseJson<Group[]>(u.user_groups, []),
    raw: u,
  }
}

const rows = computed<UserRow[]>(() => users.value.map(toRow))

function rowClassFor(u: UserRow) {
  return u.status === 'deactivated' ? 'data-table__row--muted' : ''
}

function rowHasActions(u: UserRow) {
  if (u.status === 'deactivated') return can('company_users_activate')
  return can('company_users_invite') || can('company_users_update') || can('company_users_activate')
}

// --- Data ----------------------------------------------------------------------
async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function handleAuthError(err: any): boolean {
  if (err?.response?.status === 401) {
    showDialog({ variant: 'danger', title: 'No access', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', redirect: '/' })
    return true
  }
  return false
}

async function loadReference() {
  const config = await authConfig()
  const rolesRes = await axios.get(`${baseUrl}/roles`, config)
  roles.value = [...(rolesRes.data.default_roles || []), ...(rolesRes.data.roles || [])]
  const groupsRes = await axios.get(`${baseUrl}/groups`, config)
  groups.value = groupsRes.data || []
}

async function fetchUsers() {
  refreshing.value = true
  try {
    const config = await authConfig()
    const res = await axios.get(listQuery(perPage.value, pageOffset(currentPage.value, perPage.value)), config)
    users.value = Array.isArray(res.data.users) ? res.data.users : []
    totalRows.value = Number(res.data.total_rows) || 0
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Could not load users', message: 'Something went wrong. Please refresh and try again.' })
    }
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

async function downloadUsers() {
  try {
    const config = await authConfig()
    const all = await fetchAllPages(async (count, offset) => {
      const res = await axios.get(listQuery(count, offset), config)
      return { rows: Array.isArray(res.data.users) ? res.data.users : [], total: Number(res.data.total_rows) || 0 }
    })
    const shaped = all.map(toRow)
    const inviteDate = (r: UserRow, key: 'sent_at' | 'expires_at') => {
      const i = parseJson<Invite>(r.raw.latest_invite, null)
      return i && i[key] ? formatGroupDate(i[key] as string, { dayOfWeek: false }) : ''
    }
    downloadCsv('users.csv', [
      { label: 'Name', value: (r: UserRow) => r.name },
      { label: 'Email', value: (r: UserRow) => r.email },
      { label: 'Phone', value: (r: UserRow) => r.raw.phone || '' },
      { label: 'Status', value: (r: UserRow) => r.status },
      { label: 'Role', value: (r: UserRow) => r.roleName },
      { label: 'Groups', value: (r: UserRow) => r.groups.map(g => g.name).join('; ') },
      { label: 'Last signed in', value: (r: UserRow) => (r.raw.last_login ? formatGroupDate(r.raw.last_login, { dayOfWeek: false }) : '') },
      { label: 'Invite sent', value: (r: UserRow) => inviteDate(r, 'sent_at') },
      { label: 'Invite expires', value: (r: UserRow) => inviteDate(r, 'expires_at') },
    ], shaped)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Failed to export users', timeout: 3000 })
  }
}

// --- Actions -------------------------------------------------------------------
const dialog = reactive({
  show: false,
  variant: 'warning',
  title: '',
  message: '',
  showCancel: false,
  confirmText: 'Ok',
  confirmVariant: 'primary',
  onConfirm: null as null | (() => void),
  redirect: '',
})

function showDialog(opts: Partial<typeof dialog>) {
  Object.assign(dialog, { showCancel: false, confirmText: 'Ok', confirmVariant: 'primary', onConfirm: null, redirect: '' }, opts, { show: true })
}

function onDialogConfirm() {
  dialog.show = false
  const action = dialog.onConfirm
  const redirect = dialog.redirect
  dialog.onConfirm = null
  if (action) action()
  else if (redirect) proxy.$router.push(redirect)
}

function editUser(u: UserRow) {
  selectedUser.value = u.raw
  editDrawer.value = true
}

async function resendInvite(u: UserRow) {
  loading.value = true
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/users/resend-confirmation`, { email: u.email }, config)
    proxy.$notify({ type: 'success', message: `Invite sent to ${u.email}`, icon: 'tim-icons icon-check-2' })
    await fetchUsers()
  } catch (err: any) {
    const status = err?.response?.status
    if (handleAuthError(err)) return
    if (status === 409) {
      showDialog({ variant: 'info', title: 'Already active', message: 'This user has already accepted their invite and can sign in.' })
    } else if (status === 404) {
      showDialog({ variant: 'danger', title: 'User not found', message: 'This user is not part of your company.' })
    } else {
      showDialog({ variant: 'danger', title: 'Invite not sent', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

function confirmDeactivate(u: UserRow) {
  showDialog({
    variant: 'danger',
    title: `Deactivate ${u.label}?`,
    message: `${u.email} loses access to the console immediately. You can reactivate them later from the Deactivated status filter.`,
    showCancel: true,
    confirmText: 'Deactivate',
    confirmVariant: 'danger',
    onConfirm: () => setActive(u, false),
  })
}

function confirmReactivate(u: UserRow) {
  showDialog({
    variant: 'primary',
    title: `Reactivate ${u.label}?`,
    message: `${u.email} gets access to the console again with their previous role and groups.`,
    showCancel: true,
    confirmText: 'Reactivate',
    confirmVariant: 'primary',
    onConfirm: () => setActive(u, true),
  })
}

async function setActive(u: UserRow, active: boolean) {
  loading.value = true
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/users/${active ? 'activate' : 'deactivate'}`, { email: u.email }, config)
    proxy.$notify({ type: 'success', message: active ? `${u.label} reactivated` : `${u.label} deactivated`, icon: 'tim-icons icon-check-2' })
    await fetchUsers()
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: active ? 'Not reactivated' : 'Not deactivated', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

// --- Mount ---------------------------------------------------------------------
onMounted(async () => {
  const q = proxy.$route.query || {}
  const paging = readPagingQuery(proxy.$route, 10)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 10
  currentPage.value = paging.page
  if (q.status === 'deactivated') filterValues.value = { ...filterValues.value, status: ['deactivated'] }
  if (q.invite === '1') inviteDrawer.value = true
  if (q.status || q.invite) {
    const rest = { ...q }
    delete rest.status
    delete rest.invite
    proxy.$router.replace({ path: proxy.$route.path, query: rest })
  }

  try {
    await loadReference()
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Could not load users', message: 'Something went wrong. Please refresh and try again.' })
    }
    refreshing.value = false
    return
  }
  // The filter bar emits one initial change after resolving saved views, which
  // triggers the first fetch (onFiltersChanged). Fall back if it never fires.
  setTimeout(() => { if (!didInitialFetch) { didInitialFetch = true; fetchUsers() } }, 800)
})

return { __sfc: true,proxy, companyId, baseUrl, permissions, can, myId, loading, refreshing, users, roles, groups, totalRows, inviteDrawer, editDrawer, selectedUser, perPageOptions, perPage, currentPage, didInitialFetch, tabs, onTab, sortOptions, sort, onSortChange, columns, filterValues, filterSchema, hasActiveFilters, noUsersAtAll, onFiltersChanged, onPerPageChange, onPageChange, localIsoDate, lastLoginRange, listQuery, parseJson, activityTextFor, toRow, rows, rowClassFor, rowHasActions, authConfig, handleAuthError, loadReference, fetchUsers, downloadUsers, dialog, showDialog, onDialogConfirm, editUser, resendInvite, confirmDeactivate, confirmReactivate, setActive, TabRail, InviteUserDrawer, EditUserDrawer }
}

})